// Merge-refusal POSTURE — does a refusal stand until a human acts, or does the next
// observation re-derive it? ONE table:
//
//   - `apps/worker` babysit (`babysit/escalation.ts`) escalates a STANDING refusal
//     and silently re-drives a TRANSIENT one (#4260);
//   - `./merge-refusal-remedy.ts` and `@developerz/mcp` (`merge-refusal-ref.ts`)
//     state each code's posture beside its remedy.
//
// It lives here, not beside the refusal codes, because those readers share no
// other package (`docs/idea/monorepo.md`). The code union itself stays `BabysitMergeRefusalCode`
// in `@developerz/review`; `apps/worker` re-asserts this table against it with a
// total `satisfies Record<…>`, so a code the egress gains still fails to compile
// until someone classifies it here.
//
// `standing`: a fact a later observation of the SAME head cannot change without a
// human — a maintainer's recorded hold, an `ask_before_acting` category it must decide, a reviewer's open finding, a finding template
// our parser cannot read, or GitHub's own block — or, for the three `gate_path_*`
// codes (#7275), the head's diff itself: only a changed head re-proves it, and the
// release is the CALLER's to make — a new head whose diff is a declared transform, or
// a merge by hand. Prod over the 30 days to 2026-09-16:
// 635 refused hops on 32 PRs, each retried on every pass with nobody told.
// `transient`: the next pass re-derives it; `github_refused` already has its own
// per-head bound (`github_refused_stuck`), and the review/CI waits have the
// decision's own bounds.

export const MERGE_REFUSAL_POSTURE = {
  merge_decision: 'standing',
  // 01-12: the repo's live policy forbids a merge by us; only a maintainer changes that.
  auto_merge_off: 'standing',
  // #7011: a person held this repo's merges for review on the platform; only a person
  // clearing the hold releases it (no GitHub event announces the click).
  merge_held: 'standing',
  // Plan 101 M3: who wrote the PR, or which major a bump names, is a fact of the head no
  // later observation changes — only a maintainer's `auto` for this head releases it.
  ask_before_acting: 'standing',
  // pr.auto_merge_scope: the author is outside the repo's scope — a floor of the head; only
  // the maintainer widening the scope releases it (under `not_project`, also the author's
  // repo permission dropping below write, #6360), and the author merges their own.
  author_out_of_scope: 'standing',
  // #6360: under `not_project`, the author's repo permission could not be read. Not a fact
  // of the head — failed reads are not cached, so the next pass reads it again.
  author_membership_unread: 'transient',
  // #5389: a first-time or outside author touching the gate itself (CI, policy, pipeline
  // homes). A floor of the head: no later observation and no maintainer `auto` releases
  // it — a maintainer reviews the change and merges it by hand.
  outsider_touches_gate_paths: 'standing',
  // #7785: the trust line says `external` and the live head's diff is one the intake refuses, or
  // no machine can read. Facts of the head's diff: no observation of it changes them, no `auto`
  // and no key release them, and a maintained repo closes the PR before a merge hop reaches here.
  external_change_refused: 'standing',
  external_unreadable_files: 'standing',
  // #7785 part 2: an external author's small fix passed every other rung and no strict review of
  // this head has a verdict. The next pass reads it again, and the hop mints it, once per head.
  strict_review_required: 'transient',
  // The strict review read this head and refused: a fact of the diff, only a new head changes it.
  // A maintained repo closes the PR on this answer (`apps/worker` `babysit/strict-review-hop.ts`).
  strict_review_refused: 'standing',
  // A dependency bot's release younger than the cooldown, in a race: the hop waits without a
  // refusal (`dependencyCooldownVerdict`). Time releases it, so the next pass re-derives it.
  dependency_cooldown: 'transient',
  // #7275: our App's own gate-path edit the transform proof could not prove, pinned to a sha
  // its tag no longer names, or granting more. A floor of the head: only a new head whose diff
  // is a declared transform releases it — no maintainer `auto`, no `.maintainer.yml` key. The
  // proof holds over the base/head bytes of every gate-path file, so each code below is a
  // fact of THAT diff.
  gate_path_edit_unproven: 'standing',
  gate_path_pin_unverified: 'standing',
  gate_path_widens: 'standing',
  // #7554: our App's PR leaves a scheduled brief over the schedules lane's cap. A fact of the
  // head's diff — only a new head that trims the brief releases it — and that head is a FIX
  // ROUND's to push, never a person's (`coding`, `../entities/babysit/park-class.ts`).
  brief_over_cap: 'standing',
  unresolved_findings: 'standing',
  unresolved_own_threads: 'standing',
  // B1: our review of the head was ended because no box can run it. No later observation
  // of the same head re-derives it away, so it stands. What releases it is a verdict of ours
  // landing on the head: on an AI-managed PR the babysit driver re-mints the review once per
  // head (#7586, `apps/worker` `babysit/idle-review.ts` `reReviewUnserved`); otherwise a box
  // enrolled and a fresh review (a push, or a re-review).
  review_unserved: 'standing',
  finding_format_drift: 'standing',
  github_blocks: 'standing',
  already_merged: 'transient',
  head_moved: 'transient',
  ci_not_ready: 'transient',
  checks_not_registered: 'transient',
  all_checks_tolerated: 'transient',
  protections_unmet: 'transient',
  changes_requested: 'transient',
  // #6339: the screen forced the verdict over withheld PR text. The same head's stored
  // verdict row re-reads the same hold on every pass — no observation of it changes
  // anything. Only a human act releases it: an edit to the named field, a push (a new
  // head earns a new screen), or a re-review. Contrast `changes_requested`, which a
  // reviewer's dismissal clears without the head moving.
  screen_held_pr_text: 'standing',
  review_absent: 'transient',
  external_review_pending: 'transient',
  review_in_flight: 'transient',
  human_merge_lane: 'transient',
  // Owner ruling 07-8 (2026-10-08), narrowed 2026-10-22: the driver update-branches a fleet,
  // AI-managed or org member's same-repo PR (#5238; `orgMemberPr`), whose head moves when
  // the update lands, which restarts the idle clock. An outside contributor's PR or a fork
  // is never update-branched, so nothing of ours moves its head. What is left on one
  // unchanged head past the bound — such a PR still behind, an org member's whose
  // membership could not be read, or an update GitHub keeps refusing — stands.
  stale_base: 'standing',
  // #5239: held while the base is red; the driver re-checks and the fix ask is filed.
  base_red: 'transient',
  github_refused: 'transient',
} as const;

/** A refusal code this table classifies. */
export type ClassifiedMergeRefusalCode = keyof typeof MERGE_REFUSAL_POSTURE;

// ─── the refusal a STORED PR ROW proves ──────────────────────────────────────
//
// The codes the merge GATE emits live on the audit trail (`audit_events`), not on
// the pull-request row: `pull_requests` carries no refusal-code column. So a
// reader that has only the row cannot replay the gate's decision — it can only
// read what the row itself proves, and that is the two facts below.
//
// Both are already keys of the posture table, so no code is added here: a code
// the egress gains is classified by THAT table, and this derivation names the
// subset of it a STORED ROW can carry.

/**
 * The refusal codes a stored pull-request row can prove — the subset of the
 * gate's codes the row's OWN columns carry.
 *
 * `satisfies Record<string, ClassifiedMergeRefusalCode>` is the coupling: a code
 * spelled here that the posture table does not classify fails to compile, rather
 * than becoming a code nothing can explain.
 */
export const STORED_MERGE_REFUSAL_CODES = {
  HeadMoved: 'head_moved',
  /**
   * STILL `github_blocks` after #7595 renamed the port, and that is not an oversight.
   *
   * The PORT that reads why a forge refused a merge is now `forgeBlock`
   * (`entities/babysit/forge-block.ts`), because the QUESTION is any forge's. This is the
   * stored VALUE: written to `babysit.transition` rows, matched by literal predicates in
   * `repo-health`, the MCP refusal surfaces and the overview wire. Slice 06's ruling
   * covers it exactly as it covers `author_association` and `github_repo_id` — a stored
   * value is not renamed because `forge` now qualifies it, since that is a migration over
   * audited rows to buy a nicer word and every reader would still have to match the old
   * one.
   *
   * The key mirrors the value on purpose, so the mismatch with `forgeBlock` is visible
   * here rather than discovered. Do NOT "finish" the rename across this family: the
   * spelling appears in 65 files and an audit trail.
   */
  GithubBlocks: 'github_blocks',
} as const satisfies Record<string, ClassifiedMergeRefusalCode>;

/** One of the two codes {@link storedMergeRefusalCode} can answer. */
export type StoredMergeRefusalCode =
  (typeof STORED_MERGE_REFUSAL_CODES)[keyof typeof STORED_MERGE_REFUSAL_CODES];

/**
 * The stored facts a refusal is derived from — a pull-request row's own columns.
 *
 * The four fields are named as the ROW names them, so a reader can pass the row
 * itself: `merge_decision` (the maintainer's recorded decision), the head that
 * decision was pinned to (`merge_decision_head_sha`), the row's live head
 * (`head_sha`) and GitHub's own stored block (`merge_block_state`).
 */
export type StoredMergeRefusalRow = {
  readonly mergeDecision: string | null;
  /** The head an `auto` was pinned to — an approval is OF a head (plan 101 M2). */
  readonly mergeDecisionHeadSha: string | null;
  /** The row's stored live head. Null when no head was ever projected. */
  readonly headSha: string | null;
  /** GitHub's own refusal to take the merge, when it has one. */
  readonly mergeBlockState: string | null;
};

/**
 * The refusal a STORED row proves, or null when it proves none.
 *
 * `head_moved` — the approval is pinned to a head that is no longer the live
 * one. This is the merge gate's OWN rule (`mergeDecisionPasses`,
 * `@developerz/review` `merge-gate/gate.ts`): an `auto` passes only on the head
 * it approved, so a row whose pinned head differs from its live head is an
 * approval that does not apply to what is on the branch now. Both heads must be
 * present: a row with no live head yet, or an approval pinned to nothing, proves
 * no move — it says the head is UNKNOWN, which is a different fact from "the head
 * moved" and must not be reported as one.
 *
 * `github_blocks` — GitHub's own stored block (`merge_block_state`), which the
 * platform's gates cannot clear.
 *
 * PRECEDENCE: the approval's own validity first. A row that is both stale and
 * blocked reports `head_moved`, because the approval does not apply to the live
 * head at all — the block is a fact about a head the approval never covered.
 *
 * A null answer is NOT "nothing is wrong": it means the row proves neither of
 * the two, and the reader shows the row's live waiting state (CI, mergeability,
 * the merge queue) instead.
 */
export function storedMergeRefusalCode(row: StoredMergeRefusalRow): StoredMergeRefusalCode | null {
  if (
    row.mergeDecision === 'auto' &&
    row.mergeDecisionHeadSha !== null &&
    row.headSha !== null &&
    row.mergeDecisionHeadSha !== row.headSha
  ) {
    return 'head_moved';
  }
  if (row.mergeBlockState !== null) return 'github_blocks';
  return null;
}

/**
 * The posture of a refusal code read back from STORAGE (an audit row's jsonb), where
 * it is only a string. `unclassified` is its own answer, never folded into either
 * side: a reader deciding whether to withhold a hold must treat a code it cannot
 * classify as one that may stand.
 */
export function mergeRefusalPostureOf(code: string): 'standing' | 'transient' | 'unclassified' {
  // `Object.hasOwn` narrows nothing — TS types it `(o, v) => boolean`, not as a
  // predicate on `v` — so the cast restates what the guard on the line above has
  // already proven: `code` is one of the table's own keys. A prototype key
  // (`toString`) fails `hasOwn` and lands in `unclassified`, never in the index.
  return Object.hasOwn(MERGE_REFUSAL_POSTURE, code)
    ? MERGE_REFUSAL_POSTURE[code as ClassifiedMergeRefusalCode]
    : 'unclassified';
}
